import { emptyDir } from "fs-extra";
import { Box2, Vector2 } from "three";
import { OnAnimationFrame } from "../animation/OnAnimationFrame";
import { DOMHitTest } from "../dom-tools/DOMHitTest";
import { ElementAttribute } from "../dom-tools/ElementAttribute";
import { ElementType } from "../dom-tools/ElementType";
import { OnMouseEnter, OnMouseLeave, OnMouseMove } from "../dom-tools/EventListeners";
import { HTMLEditor } from "../dom-tools/HTMLEditor";
import { InputManager } from "../dom-tools/InputManager";
import { DOMOrientation } from "../dom/DOMOrientation";
import { Range } from "../geometry/Range";
import { MathX } from "../math/MathX";

export class ImageRotaterElement extends HTMLElement
{
  _rotationX:number;
  _rotationY:number;
  _smoothCoefficient:number;
  _smoothRotationX:number;
  _smoothRotationY:number;
  _active:boolean;
  _activeStateChangeTime:number;
  _activeFadeOutDurationMS:number;
  _targetElement:HTMLElement;
  _rangeX:Range;
  _rangeY:Range;
  _reflection:SVGElement;
  
}

export class ImageRotater
{
  static readonly type = new ElementType( "image-rotater" );
  static readonly target = new ElementAttribute( "target" );
  static readonly childTarget = new ElementAttribute( "child-target" );
  static readonly childTargetParent = new ElementAttribute( "child-target-parent" );
  static readonly rangeX = new ElementAttribute( "range-x" );
  static readonly rangeY = new ElementAttribute( "range-y" );
  
  static readonly reflection = new ElementAttribute( "image-rotater-reflection" );
  static readonly initialRotation  = new ElementAttribute( "initial-rotation" );
  static readonly portraitRotation = new ElementAttribute( "portrait-rotation" );

  private static attachedGlobalListeners = false;
  private static imageRotaters:ImageRotaterElement[] = [];
  private static inputManager:InputManager;

  static applyOnDocument( onAnimationFrame:OnAnimationFrame, inputManager:InputManager )
  {
    ImageRotater.attachGlobalListener( onAnimationFrame, inputManager );
    var imageRotaters = ImageRotater.type.queryAll( document.body ) as ImageRotaterElement[];
    imageRotaters.forEach( ( e ) =>  ImageRotater.applyOnElement( e as ImageRotaterElement ) );
    ImageRotater.imageRotaters = imageRotaters;
  }

  static addElement( e:ImageRotaterElement )
  {
    ImageRotater.applyOnElement( e );
    ImageRotater.imageRotaters.push( e );
  }

  static attachGlobalListener( onAnimationFrame:OnAnimationFrame, inputManager:InputManager )
  {
    if ( ImageRotater.attachedGlobalListeners )
    {
      return;
    } 

    ImageRotater.inputManager = inputManager;
    ImageRotater.attachedGlobalListeners = true;

    onAnimationFrame.addListener(
      ()=>
      {
        //console.log( "On frame", new Date().getTime() );

        try
        {
          
          ImageRotater._updateElements();
        }
        catch( e )
        {
          console.log( e );
        }
        
      }
    );

  }

  private static _updateElements()
  {
    if ( DOMOrientation.isPortrait )
    {
      return;
    }

    var nowMS = new Date().getTime();
    ImageRotater.imageRotaters.forEach( e => ImageRotater._updateElement( e, nowMS ) );
  }

  private static _relativeBox = new Box2( new Vector2( 0, 0 ), new Vector2( 1, 1 ) );

  private static _updateElement( element:ImageRotaterElement, now:number )
  {
    var timeElapsedMS = now - element._activeStateChangeTime;

    var position = ImageRotater.inputManager.mousePosition;
    var relativeMouse = DOMHitTest.getNormalizedPosition( position, element );
    
    
    var isHovered = this._relativeBox.containsPoint( relativeMouse );

    if ( isHovered != element._active )
    {
      element._active = isHovered;
      this._relativeBox.containsPoint( relativeMouse );
      element._activeStateChangeTime = new Date().getTime();
    }

    let rangeX = element._rangeX;
    let rangeY = element._rangeY;

    if ( isHovered )
    {      
  
      element._rotationX = rangeX.sampleAt( 1 - relativeMouse.y );
      element._rotationY = rangeY.sampleAt( relativeMouse.x ); 
    }
    else
    {
      element._rotationX += 0.05 * ( rangeX.sampleAt( 0.5 ) - element._rotationX );
      element._rotationY += 0.05 * ( rangeY.sampleAt( 0.5 ) - element._rotationY );
    }

    if ( ! element._active && timeElapsedMS > element._activeFadeOutDurationMS )
    {
      return;
    }

    element._smoothRotationX += element._smoothCoefficient * ( element._rotationX - element._smoothRotationX );
    element._smoothRotationY += element._smoothCoefficient * ( element._rotationY - element._smoothRotationY );


    ImageRotater._applyRotation( element );

  }

  

  private static _applyRotation( element:ImageRotaterElement )
  {
    var value = `perspective(50vw) rotateX(${element._smoothRotationX}deg) rotateY(${element._smoothRotationY}deg)`;
    //console.log( "ApplyRotation:", value, element._smoothRotationX, element._smoothRotationY );

    element._targetElement.style.transform = value;   

    if ( ! element._reflection  )
    {
      return;
    }

    var nX = element._rangeX.normalize( element._smoothRotationX );
    var nY = element._rangeY.normalize( element._smoothRotationY );

    var amount = MathX.clamp01( MathX.clamp01( nX - 0.5) * 2 - ( 1 -  MathX.triangle( nY ) ) * 0.5 );

    element._reflection.style.opacity = amount + "";

  }

  static applyOnElement( element: ImageRotaterElement )
  {
    //console.log( "image rotater:", element );
    

    var targetElement:HTMLElement = null;
    
    if ( ImageRotater.target.in( element ) )
    {
      targetElement = ImageRotater.target.queryValueInDoc( element ) as HTMLElement;
    }

    if ( ImageRotater.childTarget.in( element ) )
    { 
      let parent:Element = element;
      
      if ( ImageRotater.childTargetParent.in( element ) )
      {
        let parentSelector = ImageRotater.childTargetParent.from( element );
        
        
        parent = HTMLEditor.findParent( element, parentSelector ) || parent;

        console.log( parentSelector, parent );
      }

      targetElement = ImageRotater.childTarget.queryValueIn( element, parent ) as HTMLElement;
    }

    if ( ! targetElement )
    {
      console.log( "no target:", element, ImageRotater.target.from( element ) );
      return;
    }

    element._targetElement = targetElement;
    element._reflection = ImageRotater.reflection.query( targetElement ) as SVGElement;

    if ( element._reflection )
    {
      element._reflection.style.opacity = 0 + "";
      element._reflection.style.pointerEvents = "none";
    }

    var rangeX = new Range( 0, 0 );
    var rangeY = new Range( 0, 0 );

    var xValues = ImageRotater.rangeX.parseNumbers( element, [ 0, 0 ] );
    var yValues = ImageRotater.rangeY.parseNumbers( element, [ 0, 0 ] );

    //console.log( "RANGES:", xValues, yValues );
    rangeX.min = xValues[ 0 ];
    rangeX.max = xValues[ 1 ];

    rangeY.min = yValues[ 0 ];
    rangeY.max = yValues[ 1 ];

    element._rangeX = rangeX;
    element._rangeY = rangeY;
    
    var initialRotation = ImageRotater.initialRotation.parseNumbers( element, [ 0.5, 0.5 ] );

    if ( initialRotation.length != 2 )
    {
      initialRotation = [ 0.5, 0.5 ];
    }
    
    //console.log( "values", rangeX, rangeY );
    

    //console.log( "INITIAL ROTATION", initialRotation );

    element._rotationX = rangeX.sampleAt( initialRotation[ 0 ] );
    element._rotationY = rangeY.sampleAt( initialRotation[ 1 ] );   

    element._smoothRotationX = rangeX.sampleAt( initialRotation[ 0 ] );
    element._smoothRotationY = rangeY.sampleAt( initialRotation[ 1 ] );   

    
    
    element._active = false;
    element._activeFadeOutDurationMS = 2000;
    element._smoothCoefficient = 0.1;
     
    if ( DOMOrientation.isPortrait )
    {
      var portraitRotation = ImageRotater.portraitRotation.parseNumbers( element, [ 0.5, 0.5 ] );

      if ( portraitRotation.length != 2 )
      {
        portraitRotation = [ 0.5, 0.5 ];
      }      

      element._smoothRotationX = rangeX.sampleAt( portraitRotation[ 0 ] );
      element._smoothRotationY = rangeY.sampleAt( portraitRotation[ 1 ] );

      //console.log( "portrait rotation", portraitRotation,ImageRotater.portraitRotation.from( element), element._smoothRotationX, element._smoothRotationY )
    }

    ImageRotater._applyRotation( element );

    /*OnMouseMove.add(
      element, ( e )=>
      {
        
        var relativeMouse = DOMHitTest.getNormalizedPointerPosition( e, element );

        element._rotationX = rangeX.sampleAt( 1 - relativeMouse.y );
        element._rotationY = rangeY.sampleAt( relativeMouse.x );  
      } 
    );

    OnMouseLeave.add( 
      element, ()=>
      {
        element._active = false;
        element._activeStateChangeTime = new Date().getTime();
      }
    );

    OnMouseEnter.add( 
      element, ()=>
      {
        element._active = true;
        element._activeStateChangeTime = new Date().getTime();
      }
    );
    */
  }
}